// ─────────────────────────────────────────────
//  parseGlobalSheet — one pass of the daily "Prelim Anes Assignments" sheet
//  split into ALL THREE campuses (Wesley Chapel / Tampa / Carrollwood).
//
//  The sheet is one flat grid for every AdventHealth Tampa-area hospital; the
//  OR-name prefix encodes the campus (see domain/sheetRoomFamily). The
//  facility-specific import filters to ONE family; the GLOBAL import runs the
//  same parser once per family and produces a per-facility draft, so a single
//  click can stage every campus's day at once.
//
//  Pure — no Firebase, no network. The component (GlobalSheetImport) fetches
//  the values, loads each facility's rooms, and applies the drafts.
// ─────────────────────────────────────────────

import { parsePrelimAssignments } from "./parsePrelimAssignments";
import { parseAttendingCallList } from "./attendingCallList";
import { parseNotesLocums } from "./notesLocums";
import { toSheetDraftGrid } from "./toSheetDraftGrid";
import { facilityCoverage, sumCoverage } from "./importCoverage";
import { roomFamilyForFacility } from "../../domain/sheetRoomFamily";

// The campuses the global import writes, in apply order — a FALLBACK for this
// deployment, not a source of truth. A different customer has entirely
// different campuses, so callers pass their own ids (ctx.facilityIds, derived
// from the live `facilities` docs). Leaving this hardcoded meant a new
// hospital's sheet parsed into zero drafts, with no error to explain it.
export const GLOBAL_IMPORT_FACILITY_IDS = ["default", "tampa", "carrollwood"];

// Which facilities to split this sheet into. Explicit ids win; otherwise fall
// back to the built-ins so existing callers behave exactly as before.
export function importFacilityIds(facilityIds) {
  const ids = (facilityIds || []).filter(Boolean);
  return ids.length ? [...new Set(ids)] : [...GLOBAL_IMPORT_FACILITY_IDS];
}

/**
 * Split the sheet into one reviewable draft per facility.
 *
 * @param {Array<Array<string>>} values — raw Sheets 2D values
 * @param {object} ctx
 *   @param {Array} ctx.doctors  — FULL doctor pool (staff master)
 *   @param {Array} ctx.crnas    — FULL CRNA/AA pool (staff master)
 *   @param {Object<string,Array>} ctx.roomsByFacility — live room docs per facility id
 * @returns {{ mdCall, byFacility: { [fid]: { found, parsedRows, draft,
 *   gridFlags, skipped, newStaff, unassigned } } }}
 */
export function parseGlobalSheet(values, { doctors = [], crnas = [], roomsByFacility = {}, configByFacility = {}, facilityIds = null, identity = {} } = {}) {
  // MD call list spans every hospital's attending column — parsed once.
  const mdCall = parseAttendingCallList(values);
  // Locum coverage from the NOTES row under the importer marker (Day Hawk /
  // Night Float, up to LUT) — resolved against the doctor pool once.
  const notesLocums = parseNotesLocums(values, { doctors });
  const byFacility = {};
  for (const fid of importFacilityIds(facilityIds)) {
    const { found, rows, columnSource } = parsePrelimAssignments(values, {
      // configByFacility carries each facility's own room-family pattern
      // (blocker #4), so a campus can define its OR prefixes without a code
      // change; falls back to the built-in map for the known AdventHealth ones.
      roomFamily: roomFamilyForFacility(fid, { configByFacility }),
    });
    const grid = toSheetDraftGrid(rows, {
      doctors,
      crnas,
      surgeons: [],
      rooms: roomsByFacility[fid] || [],
      // ⚠️ THE ALIAS RUNG IS FACILITY-SCOPED. Two campuses can hold two
      // different colleagues both written `J. Patel`; an alias confirmed at one
      // must not answer for the other. The QGenda index is workbook-wide.
      identity: {
        aliases: identity.aliases || [],
        qgenda: identity.qgenda || null,
        facilityId: fid,
      },
      sheetValues: values,
    });
    byFacility[fid] = {
      found,
      parsedRows: rows,
      draft: grid.rows,
      gridFlags: grid.gridFlags,
      skipped: grid.skipped,
      // ⚠️ NOT `newStaff`. Questions, not a creation list — see toSheetDraftGrid.
      unresolved: grid.unresolved || { people: [], doctorCodes: [] },
      placed: grid.placed || 0,
      unassigned: grid.unassigned || [],
      // ⚠️ WHAT THIS IMPORT COULD NOT READ, verbatim, per cell. The manual
      // importer shows unmatched staff before Apply; the scheduled one applies
      // blind AND full-resets the board, so its refusals have to be carried
      // out of the parser rather than dropped inside it.
      review: grid.review || [],
      // Which answer the column map used — "header" or the historical "default"
      // positions. A silent fall back to positions is how a template change
      // becomes a silent misread.
      columnSource,
    };
    // ⚠️ THE REPORT IS BUILT BY THE PARSER, NOT BY A SCREEN. The nightly job
    // never opens a screen, and its board reset is what makes an unseen refusal
    // expensive — so the numbers and the queue have to exist before anybody
    // decides whether to look at them.
    byFacility[fid].coverage = facilityCoverage(byFacility[fid], crnas, fid);
  }
  const coverage = sumCoverage(Object.values(byFacility).map((f) => f.coverage));
  return { mdCall, notesLocums, byFacility, coverage };
}

// Layout guard for the UNATTENDED nightly import: true when the grid contains
// the Prelim assignments header row ("OR# Surgeon Procedure Duration Attending
// AHP"). Mirrors parsePrelimAssignments' header detection, so "header present"
// and "header skipped as non-data" can never disagree. A grid without it means
// the department changed the template (or the wrong tab was read) — the
// scheduled import must FAIL LOUDLY before resetting any board, not silently
// parse garbage.
export function hasAssignmentsHeader(values) {
  return (Array.isArray(values) ? values : []).some((row) => {
    const joined = (row || []).map((v) => String(v ?? "").trim()).join(" ").toLowerCase();
    return /\bprocedure\b/.test(joined) && /(surgeon|attending|duration|ahp)/.test(joined);
  });
}

// ── WHY THERE IS NO CREATION LIST HERE ANY MORE ─────────────────────────────
//
// `unionNewCrnas`, `isPlausibleCrnaName`, `isPlausibleDoctorCode` and
// `partitionNewStaff` existed to hand the unattended job a set of names to
// create. On the live sheet of 2026-08-20 that set held 22 real clinicians'
// real names — and the honest cell reader made it WORSE, 20 → 22, because the
// three strings it used to quarantine were mangled cells hiding five actual
// people. Twenty-two staff documents would have been minted at 00:30 by a job
// nobody watched, on top of the 23 duplicate colleagues two days had already
// produced. One person already exists five times.
//
// Product owner, 2026-08-20: **"never create — always ask a human."**
//
// So the pipeline no longer produces a creation list at ALL. `toSheetDraftGrid`
// returns `unresolved.people` — questions carrying the verbatim cell and the
// near-misses — and `importCoverage` turns them into a review queue a person
// clears. DELETING these exports is the guard: there is nothing left for a
// nightly job to pass to `staff.add()`, so no later edit can quietly re-point
// one at it. `unresolved.doctorCodes` is a REPORT of codes no doctor carries;
// it is rendered, never created from.

// Merge a buildBoardWrites roster into the facility's EXISTING daily board —
// sheet members upsert by id, manually-added roster members survive, and the
// sheet's AIC only overrides when the sheet actually named one.
export function mergeRosterPatch(existing = {}, built = {}) {
  const upsert = (base, add) => {
    const out = (base || []).map((x) => ({ ...x }));
    for (const m of add || []) {
      if (!m || !m.id) continue;
      const i = out.findIndex((x) => x.id === m.id);
      if (i >= 0) out[i] = { ...out[i], ...m };
      else out.push({ ...m });
    }
    return out;
  };
  const patch = {
    supervisors: upsert(existing.supervisors, built.supervisors),
    crnas: upsert(existing.crnas, built.crnas),
    mdCallList: built.mdCallList?.length ? built.mdCallList : existing.mdCallList || [],
  };
  if (built.aicId) {
    patch.aicId = built.aicId;
    patch.aicName = built.aicName || "";
  }
  return patch;
}
